/**
 * Командная секция СОУТ в карточке заказа (canon service-sout «Результат СОУТ по рабочему месту»).
 * Просмотр анкеты клиента (read-only) + ввод результата по местам: класс УТ (выпадающий список) +
 * общая «Дата завершения» заказа + чекбокс «Декларация» + комментарий.
 *
 * p2-service-sout (D4, §5/§7.5): запись результата — черновая операция, доступная когда заказ уже
 * «В работе» или «Готово». До принятия в работу форма недоступна.
 *
 * centrika-docs!74/75/76 (объединённо):
 *  - Роли после завершения (!74): в «Готово» изменять/пересохранять результаты может ТОЛЬКО
 *    администратор; сотруднику Центрики — просмотр (поля недоступны, кнопки нет). Enforcement
 *    дублируется на сервере (fail-closed).
 *  - Общая «Дата завершения» (!75): одно обязательное поле над списком РМ, подставляется в каждую
 *    строку; срок действия считается от неё; отдельного изменения даты в строке нет.
 *  - Класс УТ + декларация + срок (!76): «Класс УТ» — выпадающий список (1/2/3.1/3.2/3.3/3.4/4, по
 *    умолчанию не выбран); «Декларация» доступна только для «1»/«2»; бессрочность даёт ТОЛЬКО
 *    отмеченная декларация (сам класс — больше нет); смена класса с «1»/«2» сбрасывает декларацию.
 *    Столбец «Историческая СОУТ» убран.
 *
 * centrika-docs!99: столбец «Документ/отчёт» убран вместе с загрузкой и скачиванием отчётов по
 * отдельным рабочим местам («планируем это делать потом при выгрузке из Аттестации 2.0»).
 * Серверный механизм файлов (`sout_line_id`, тип «Отчёты СОУТ») не тронут — снят только UI.
 *
 * centrika-docs!90: первая колонка таблицы — «N раб.места», необязательное текстовое поле номера
 * рабочего места по результатам ТЕКУЩЕГО заказа. Пустое значение не мешает ни сохранению, ни
 * завершению заказа; «Сохранить результаты» пишет номер вместе с результатом, и он виден в
 * карточке места («Сведения по СОУТ»). Доступность ввода — по общим правилам правки результатов.
 *
 * sout-results-statement-import: над таблицей — «Загрузить сводную ведомость» (тем, кто может править
 * результаты). Разобранная ведомость подставляет в черновики строк «N раб.места» и класс УТ (смена
 * класса сбрасывает декларацию по тому же правилу, что ручной выбор); сохраняются значения обычной
 * кнопкой «Сохранить результаты». Комментарий из ведомости не заполняется.
 *
 * sout-results-declaration-and-statement-date (решение заказчика):
 *  - «Дата завершения» берётся из даты составления сводной ведомости (если она в ведомости есть);
 *    обязательность даты, done-гейт и расчёт срока не меняются.
 *  - «Загрузить декларацию»: места декларации сверяются с таблицей по «N раб.места» + должности
 *    (должность — в формулировке ведомости, если она загружалась, иначе анкеты) и получают
 *    «Декларацию». Отметки только добавляются; сохраняются кнопкой «Сохранить результаты».
 */
import { useEffect, useState } from 'react'
import { teamApi, type SoutAnketa, type SoutResult, type TeamOrderDetail } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { useAuth } from '@/app/auth-context'
import {
  SOUT_DECLARATION_APPLIED_HINT,
  SOUT_HAZARD_CLASSES,
  SOUT_STATEMENT_APPLIED_HINT,
  soutResultDeclarationAllowed,
  soutResultDeclarationChecked,
  soutResultValidUntilPreview,
  type SoutDeclarationTableRow,
  type SoutStatementApplyItem,
} from '@/lib/sout'
import { SoutAnketaView } from './sout-anketa-view'
import { SoutDeclarationImport } from './sout-declaration-import'
import { SoutStatementImport } from './sout-statement-import'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { DateInput } from '@/design-system/components/primitives/date-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

interface RowDraft {
  hazardClass: string
  declaration: boolean
  comment: string
  /**
   * «N раб.места» (centrika-docs!90) — номер рабочего места по результатам ТЕКУЩЕГО заказа.
   * Строка, а не число: ведущие нули значимы («007» остаётся «007»).
   */
  rmNumber: string
}

/** Пустой черновик строки результата — единая точка правды по составу полей. */
function emptyDraft(): RowDraft {
  return { hazardClass: '', declaration: false, comment: '', rmNumber: '' }
}

/**
 * Черновик с новым классом УТ. Смена класса на недоступный для декларации (не «1»/«2») сбрасывает
 * чекбокс (centrika-docs!76) — одинаково для ручного выбора и подстановки из сводной ведомости.
 */
function withHazardClass(draft: RowDraft, hazardClass: string): RowDraft {
  const declaration = soutResultDeclarationAllowed(hazardClass) ? draft.declaration : false
  return { ...draft, hazardClass, declaration }
}

export function TeamSoutSection({ orderId }: { orderId: string }) {
  const { context } = useAuth()
  const isAdmin = context?.user.role === 'admin'

  const [anketa, setAnketa] = useState<SoutAnketa | null>(null)
  const [results, setResults] = useState<SoutResult[]>([])
  const [order, setOrder] = useState<TeamOrderDetail | null>(null)
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({})
  // Общая «Дата завершения» заказа (centrika-docs!75) — одна на весь заказ, подставляется в каждую
  // строку РМ. Инициализируется из даты уже сохранённого результата (все результаты делят её).
  const [completionDate, setCompletionDate] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // В черновики подставлены значения из сводной ведомости и ещё не сохранены — показываем подсказку.
  const [statementApplied, setStatementApplied] = useState(false)
  // В черновиках отмечена декларация из файла декларации и ещё не сохранена — показываем подсказку.
  const [declarationApplied, setDeclarationApplied] = useState(false)
  // Должности мест в формулировке последней подставленной ведомости (`lineId` → должность): по ним
  // декларация сверяет «номер + должность». Не сохраняются — живут до перезагрузки страницы.
  const [statementPositions, setStatementPositions] = useState<Record<string, string>>({})

  function load(signal?: AbortSignal) {
    setLoading(true)
    setError(null)
    Promise.all([
      teamApi.sout.getAnketa(orderId, signal),
      teamApi.sout.getResults(orderId, signal),
      teamApi.orders.get(orderId, signal),
    ])
      .then(([ank, res, ord]) => {
        if (signal?.aborted) return
        setAnketa(ank)
        setResults(res)
        setOrder(ord)
        const next: Record<string, RowDraft> = {}
        let commonDate = ''
        for (const line of ank.lines) {
          const existing = res.find(
            (r) => r.lineId === line.id || (r.workplaceId && r.workplaceId === line.workplaceId),
          )
          next[line.id] = {
            hazardClass: existing?.hazardClass ?? '',
            // Декларация восстанавливается из сохранённых значений (сервер также присылает
            // declaration; фолбэк на деривацию — для результатов из старых ответов).
            declaration: existing
              ? (existing.declaration ?? soutResultDeclarationChecked(existing))
              : false,
            comment: existing?.comment ?? '',
            // Сохранённый «N раб.места» восстанавливается при повторном открытии заказа
            // (centrika-docs!90); номера нет — поле пустое.
            rmNumber: existing?.rmNumber ?? '',
          }
          if (!commonDate && existing?.conductedOn) commonDate = existing.conductedOn
        }
        setDrafts(next)
        setCompletionDate(commonDate)
      })
      .catch((err) => {
        if (!signal?.aborted) setError(getErrorMessage(err, 'Не удалось загрузить анкету СОУТ'))
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false)
      })
  }

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  // Смена класса, недоступного для декларации (не «1»/«2»), автоматически сбрасывает чекбокс
  // (centrika-docs!76): «если после выбора декларации класс изменён … чекбокс сбрасывается».
  function setHazardClass(lineId: string, hazardClass: string) {
    setDrafts((prev) => ({
      ...prev,
      [lineId]: withHazardClass(prev[lineId] ?? emptyDraft(), hazardClass),
    }))
  }

  // Подстановка из сводной ведомости: номер — всегда, класс — если распознан, «Дата завершения» — дата
  // составления ведомости, если она в ведомости есть.
  function applyStatement(items: SoutStatementApplyItem[], compiledOn: string | null) {
    setDrafts((prev) => {
      const next = { ...prev }
      for (const item of items) {
        const cur = { ...(next[item.lineId] ?? emptyDraft()), rmNumber: item.rmNumber }
        next[item.lineId] = item.hazardClass === null ? cur : withHazardClass(cur, item.hazardClass)
      }
      return next
    })
    setStatementPositions((prev) => {
      const next = { ...prev }
      for (const item of items) next[item.lineId] = item.position
      return next
    })
    if (compiledOn) setCompletionDate(compiledOn)
    setSaveError(null)
    setStatementApplied(true)
  }

  // Отметка из декларации: только добавляет «Декларацию» и только там, где её допускает класс
  // (окно уже отсеяло остальные места — проверка повторена на случай смены класса).
  function applyDeclaration(lineIds: string[]) {
    setDrafts((prev) => {
      const next = { ...prev }
      for (const lineId of lineIds) {
        const cur = next[lineId]
        if (cur && soutResultDeclarationAllowed(cur.hazardClass)) {
          next[lineId] = { ...cur, declaration: true }
        }
      }
      return next
    })
    setSaveError(null)
    setDeclarationApplied(true)
  }

  function setDeclaration(lineId: string, declaration: boolean) {
    setDrafts((prev) => ({ ...prev, [lineId]: { ...prev[lineId], declaration } }))
  }

  function setComment(lineId: string, comment: string) {
    setDrafts((prev) => ({ ...prev, [lineId]: { ...prev[lineId], comment } }))
  }

  function setRmNumber(lineId: string, rmNumber: string) {
    setDrafts((prev) => ({ ...prev, [lineId]: { ...prev[lineId], rmNumber } }))
  }

  function resultFor(lineId: string, workplaceId: string | null): SoutResult | undefined {
    return results.find((r) => r.lineId === lineId || (r.workplaceId && r.workplaceId === workplaceId))
  }

  // D4 (§5): место «заполнено» при непустых сохранённых классе УТ и дате — тот же критерий,
  // что серверный done-гейт (store.SoutResultsComplete); подсказка здесь опережает отказ сервера.
  function isFilled(lineId: string, workplaceId: string | null): boolean {
    const r = resultFor(lineId, workplaceId)
    return Boolean(r?.hazardClass && r?.conductedOn)
  }

  const status = order?.dataStatus
  const isDone = status === 'done'
  const isInProgress = status === 'in_progress'
  // Форма (таблица) показывается «В работе»/«Готово»; поля редактируемы «В работе» всегда, а в
  // «Готово» — только администратору (centrika-docs!74). Иначе (review и т.п.) форма недоступна.
  const formAvailable = isInProgress || isDone
  const canEdit = isInProgress || (isDone && isAdmin)
  const viewOnlyAfterDone = isDone && !isAdmin

  const missingCount = anketa ? anketa.lines.filter((l) => !isFilled(l.id, l.workplaceId)).length : 0
  // Гейт завершения (centrika-docs!75): нельзя завершить, если общая дата завершения не заполнена
  // ИЛИ не по всем РМ сохранён класс. Предупреждение опережает серверный отказ.
  const completionGateBlocked = !completionDate || missingCount > 0

  /**
   * Предотправочная проверка (issue #9): сохраняются только строки с классом УТ И общей
   * датой завершения. Если таких строк нет, раньше на сервер уходил пустой `items`, а
   * пользователь получал английское «validation failed». Теперь форма сама объясняет,
   * чего не хватает; введённые значения при этом не сбрасываются.
   */
  function resultsGap(): string | null {
    if (!anketa) return null
    const withClass = anketa.lines.filter((l) => drafts[l.id]?.hazardClass.trim())
    if (!completionDate && withClass.length === 0) {
      return 'Заполните обязательные поля: укажите дату завершения и класс УТ хотя бы для одного рабочего места.'
    }
    if (!completionDate) {
      return 'Заполните обязательные поля: укажите дату завершения — она общая для всего заказа.'
    }
    if (withClass.length === 0) {
      return 'Заполните обязательные поля: выберите класс УТ хотя бы для одного рабочего места.'
    }
    return null
  }

  async function save() {
    if (!anketa) return
    setSaveError(null)
    const gap = resultsGap()
    if (gap) {
      setSaveError(gap)
      return
    }
    setSaving(true)
    try {
      // Сохраняются только строки с заполненными классом И общей датой завершения; дата в каждый
      // результат пишется общая (centrika-docs!75). Декларация передаётся как есть — сервер
      // авторитетно пересчитывает срок/бессрочность.
      const items = anketa.lines
        .map((line) => ({ line, draft: drafts[line.id] }))
        .filter(({ draft }) => draft && draft.hazardClass.trim() && completionDate)
        .map(({ line, draft }) => ({
          lineId: line.id,
          workplaceId: line.workplaceId ?? undefined,
          hazardClass: draft.hazardClass.trim(),
          conductedOn: completionDate,
          declaration: soutResultDeclarationAllowed(draft.hazardClass) ? draft.declaration : false,
          comment: draft.comment.trim() || undefined,
          // «N раб.места» (centrika-docs!90) отправляется ВСЕГДА, в т.ч. пустой строкой:
          // очищенное поле обязано очистить сохранённый номер (`undefined` бы его сохранил).
          rmNumber: draft.rmNumber.trim(),
        }))
      const updated = await teamApi.sout.putResults(orderId, { items })
      setResults(updated)
      setStatementApplied(false)
      setDeclarationApplied(false)
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось сохранить результаты'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Card header="Анкета СОУТ" className="mt-4">
        <p className="py-2 text-txt-secondary">Загрузка…</p>
      </Card>
    )
  }
  if (error || !anketa) {
    return (
      <Alert variant="danger" className="mt-4">
        {error ?? 'Анкета недоступна'}
      </Alert>
    )
  }

  // Строки таблицы для сверки декларации: текущие (в т.ч. несохранённые) номер и класс места.
  const declarationRows: SoutDeclarationTableRow[] = anketa.lines.map((line) => {
    const draft = drafts[line.id] ?? emptyDraft()
    return {
      lineId: line.id,
      rmNumber: draft.rmNumber,
      positions: [statementPositions[line.id] ?? '', line.conditions.position],
      hazardClass: draft.hazardClass,
    }
  })

  function validUntilText(lineId: string): string {
    const draft = drafts[lineId]
    if (!draft) return '—'
    const preview = soutResultValidUntilPreview(draft.hazardClass, completionDate, draft.declaration)
    if (preview.kind === 'perpetual') return 'бессрочно'
    if (preview.kind === 'date') return formatDate(preview.date)
    return '—'
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      <Card header="Анкета СОУТ (данные клиента)">
        <SoutAnketaView anketa={anketa} orderId={orderId} orderNumber={order?.number ?? ''} />
      </Card>

      <Card header="Результат СОУТ по местам">
        {!formAvailable ? (
          <Alert variant="info" title="Форма недоступна" className="mb-3">
            Результаты вносятся после принятия заказа в работу («В работе» или «Готово»).
          </Alert>
        ) : null}
        {viewOnlyAfterDone ? (
          <Alert variant="info" title="Только просмотр" className="mb-3">
            Заказ завершён. Изменять результаты после завершения может только администратор.
          </Alert>
        ) : null}
        {canEdit && completionGateBlocked ? (
          <Alert variant="info" title="Не всё заполнено" className="mb-3">
            {!completionDate
              ? 'Укажите дату завершения и заполните результаты по всем рабочим местам — без этого заказ нельзя перевести в «Готово».'
              : `Не заполнены результаты по ${missingCount} ${
                  missingCount === 1 ? 'рабочему месту' : 'рабочим местам'
                } — без них заказ нельзя перевести в «Готово».`}
          </Alert>
        ) : null}
        {saveError ? (
          <Alert variant="danger" className="mb-3">
            {saveError}
          </Alert>
        ) : null}
        {canEdit && statementApplied ? (
          <Alert variant="info" className="mb-3">
            {SOUT_STATEMENT_APPLIED_HINT}
          </Alert>
        ) : null}
        {canEdit && declarationApplied ? (
          <Alert variant="info" className="mb-3">
            {SOUT_DECLARATION_APPLIED_HINT}
          </Alert>
        ) : null}
        {anketa.lines.length === 0 ? (
          <p className="py-2 text-txt-secondary">Нет рабочих мест для ввода результата.</p>
        ) : (
          <>
            {canEdit ? (
              <div className="mb-4 flex flex-wrap items-start gap-2">
                <SoutStatementImport
                  orderId={orderId}
                  lines={anketa.lines}
                  current={drafts}
                  completionDate={completionDate}
                  onApply={applyStatement}
                />
                <SoutDeclarationImport
                  orderId={orderId}
                  lines={anketa.lines}
                  rows={declarationRows}
                  onApply={applyDeclaration}
                />
              </div>
            ) : null}
            {formAvailable ? (
              <div className="mb-4 max-w-xs">
                <label className="mb-1 block text-13 font-medium text-txt" htmlFor="sout-completion-date">
                  Дата завершения
                </label>
                <DateInput
                  value={completionDate}
                  disabled={!canEdit}
                  aria-label="Дата завершения"
                  aria-required
                  onChange={setCompletionDate}
                />
                <p className="mt-1 text-12 text-txt-tertiary">
                  Общая для всего заказа — подставляется в каждое рабочее место.
                </p>
              </div>
            ) : null}
            <div className="overflow-x-auto">
              <DataTable>
                <DataTableHead>
                  <tr>
                    <DataTableHeaderCell>N раб.места</DataTableHeaderCell>
                    <DataTableHeaderCell>Рабочее место</DataTableHeaderCell>
                    <DataTableHeaderCell>Класс УТ</DataTableHeaderCell>
                    <DataTableHeaderCell>Декларация</DataTableHeaderCell>
                    <DataTableHeaderCell>Дата завершения</DataTableHeaderCell>
                    <DataTableHeaderCell>Действует до</DataTableHeaderCell>
                    <DataTableHeaderCell>Класс клиента</DataTableHeaderCell>
                    <DataTableHeaderCell>Комментарий</DataTableHeaderCell>
                  </tr>
                </DataTableHead>
                <DataTableBody>
                  {anketa.lines.map((line, i) => {
                    const draft = drafts[line.id] ?? emptyDraft()
                    const existing = resultFor(line.id, line.workplaceId)
                    const declarationAllowed = soutResultDeclarationAllowed(draft.hazardClass)
                    return (
                      <DataTableRow key={line.id}>
                        <DataTableCell>
                          <Input
                            value={draft.rmNumber}
                            disabled={!canEdit}
                            className="w-28"
                            aria-label={`N раб.места места №${i + 1}`}
                            onChange={(e) => setRmNumber(line.id, e.target.value)}
                            placeholder="Необязательно"
                          />
                        </DataTableCell>
                        <DataTableCell className="font-medium text-txt">
                          №{i + 1} · {line.conditions.position || '—'}
                        </DataTableCell>
                        <DataTableCell>
                          <Select
                            value={draft.hazardClass}
                            disabled={!canEdit}
                            onValueChange={(v) => setHazardClass(line.id, v)}
                          >
                            <SelectTrigger
                              className="w-24"
                              aria-label={`Класс УТ места №${i + 1}`}
                            >
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              {SOUT_HAZARD_CLASSES.map((c) => (
                                <SelectItem key={c} value={c}>
                                  {c}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </DataTableCell>
                        <DataTableCell>
                          <Checkbox
                            checked={draft.declaration}
                            disabled={!canEdit || !declarationAllowed}
                            aria-label={`Декларация места №${i + 1}`}
                            onCheckedChange={(v) => setDeclaration(line.id, v === true)}
                          />
                        </DataTableCell>
                        <DataTableCell>{formatDate(completionDate)}</DataTableCell>
                        <DataTableCell>{validUntilText(line.id)}</DataTableCell>
                        <DataTableCell>{existing?.originalClass || '—'}</DataTableCell>
                        <DataTableCell>
                          <Input
                            value={draft.comment}
                            disabled={!canEdit}
                            aria-label={`Комментарий места №${i + 1}`}
                            onChange={(e) => setComment(line.id, e.target.value)}
                            placeholder="Необязательно"
                          />
                        </DataTableCell>
                      </DataTableRow>
                    )
                  })}
                </DataTableBody>
              </DataTable>
            </div>
            <p className="mt-3 text-12 text-txt-tertiary">
              Срок действия — 5 лет от даты завершения; «бессрочно» — только при отмеченной
              декларации для класса 1–2 (считает система).
            </p>
            {canEdit ? (
              <Button className="mt-3" disabled={saving} onClick={() => void save()}>
                {saving ? 'Сохранение…' : 'Сохранить результаты'}
              </Button>
            ) : null}
          </>
        )}
      </Card>
    </div>
  )
}
